/**
 * Customer-statement derivations (mirrors hindsight-accounting-suite Sprint 2).
 *
 * Three IFRS-aligned archetypes computed client-side from invoice + payment
 * rows the Billing tab already loads:
 *
 *   - balance_forward: opening → activity → closing with running balance.
 *     For AD-13 strict prepaid the balance generally returns to zero after
 *     each cycle; useful as an audit-trail view.
 *   - open_item:       outstanding invoices grouped by aging bucket
 *                      (Current / 1–30 / 31–60 / 61–90 / 91–120 / 120+).
 *   - activity:        chronological (date, type, description, amount) over
 *                      the chosen period — closest to "transaction listing".
 *
 * No new EF or table is required. The aging-bucket layout matches the
 * reference project so a future shared component can render either source.
 */

import type {
  AdminInvoiceRow,
  AdminPaymentRow,
} from './adminInvoiceService';

export type AgingBucketKey =
  | 'current'
  | 'days_1_30'
  | 'days_31_60'
  | 'days_61_90'
  | 'days_91_120'
  | 'days_120_plus';

export interface AgingBuckets {
  current: number;
  days_1_30: number;
  days_31_60: number;
  days_61_90: number;
  days_91_120: number;
  days_120_plus: number;
  total: number;
}

export interface AgingBucketLabel {
  key: AgingBucketKey;
  label: string;
  accent: 'slate' | 'amber' | 'orange' | 'red' | 'red-deep';
}

export const AGING_BUCKET_LAYOUT: AgingBucketLabel[] = [
  { key: 'current', label: 'Current', accent: 'slate' },
  { key: 'days_1_30', label: '1–30', accent: 'slate' },
  { key: 'days_31_60', label: '31–60', accent: 'amber' },
  { key: 'days_61_90', label: '61–90', accent: 'orange' },
  { key: 'days_91_120', label: '91–120', accent: 'red' },
  { key: 'days_120_plus', label: '120+', accent: 'red-deep' },
];

export const EMPTY_AGING: AgingBuckets = {
  current: 0,
  days_1_30: 0,
  days_31_60: 0,
  days_61_90: 0,
  days_91_120: 0,
  days_120_plus: 0,
  total: 0,
};

export type StatementArchetype = 'balance_forward' | 'open_item' | 'activity';

export interface BalanceForwardRow {
  date: string;
  description: string;
  reference: string | null;
  debit: number;
  credit: number;
  running_balance: number;
}

export interface BalanceForwardStatement {
  archetype: 'balance_forward';
  opening_balance: number;
  closing_balance: number;
  rows: BalanceForwardRow[];
}

export interface OpenItem {
  invoice_id: string;
  invoice_number: string;
  invoice_date: string;
  due_date: string | null;
  total: number;
  paid: number;
  outstanding: number;
  bucket: AgingBucketKey;
}

export interface OpenItemStatement {
  archetype: 'open_item';
  outstanding_total: number;
  items: OpenItem[];
}

export interface ActivityEvent {
  event_at: string;
  event_type: 'invoice_issued' | 'invoice_paid' | 'payment_received';
  description: string;
  reference: string | null;
  /** Negative for credits/payments, positive for invoices. */
  signed_amount: number;
}

export interface ActivityStatement {
  archetype: 'activity';
  events: ActivityEvent[];
}

export type CustomerStatement =
  | BalanceForwardStatement
  | OpenItemStatement
  | ActivityStatement;

export interface StatementBundle {
  aging: AgingBuckets;
  balance_forward: BalanceForwardStatement;
  open_item: OpenItemStatement;
  activity: ActivityStatement;
}

const OUTSTANDING_STATES: ReadonlySet<string> = new Set([
  'unpaid',
  'grace',
  'suspended',
]);

function bucketForDaysOverdue(daysOverdue: number): AgingBucketKey {
  if (daysOverdue <= 0) return 'current';
  if (daysOverdue <= 30) return 'days_1_30';
  if (daysOverdue <= 60) return 'days_31_60';
  if (daysOverdue <= 90) return 'days_61_90';
  if (daysOverdue <= 120) return 'days_91_120';
  return 'days_120_plus';
}

function diffDays(from: string, to: Date): number {
  const a = new Date(from + 'T00:00:00');
  const b = new Date(to.getFullYear(), to.getMonth(), to.getDate());
  const ms = b.getTime() - a.getTime();
  return Math.floor(ms / 86_400_000);
}

function inPeriod(
  iso: string | null,
  start: Date,
  end: Date
): boolean {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return t >= start.getTime() && t <= end.getTime();
}

export const DEFAULT_PERIODS: Record<
  'last30' | 'last90' | 'ytd',
  () => { start: Date; end: Date }
> = {
  last30: () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    return { start, end };
  },
  last90: () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 90);
    return { start, end };
  },
  ytd: () => {
    const end = new Date();
    const start = new Date(end.getFullYear(), 0, 1);
    return { start, end };
  },
};

/**
 * Compute aging from outstanding invoices using `due_date` (or `period_end`
 * if missing — should not happen in production but is defensive).
 */
function computeAging(
  invoices: AdminInvoiceRow[],
  payments: AdminPaymentRow[],
  today: Date
): AgingBuckets {
  const buckets: AgingBuckets = { ...EMPTY_AGING };
  for (const inv of invoices) {
    const state = inv.lifecycle_state ?? inv.status;
    if (!OUTSTANDING_STATES.has(state)) continue;
    const dueDate = inv.due_date ?? inv.period_end ?? inv.issue_date;
    const paid = payments
      .filter((p) => p.invoice_id === inv.id && p.status === 'success')
      .reduce((sum, p) => sum + p.amount, 0);
    const outstanding = inv.total - paid;
    if (outstanding <= 0) continue;
    const days = diffDays(dueDate, today);
    const key = bucketForDaysOverdue(days);
    buckets[key] += outstanding;
    buckets.total += outstanding;
  }
  return buckets;
}

function computeBalanceForward(
  invoices: AdminInvoiceRow[],
  payments: AdminPaymentRow[],
  start: Date,
  end: Date
): BalanceForwardStatement {
  // Opening balance = sum of (invoice issued before start) − (payments before start)
  let opening = 0;
  for (const inv of invoices) {
    const issuedAt = new Date(inv.issue_date + 'T00:00:00');
    if (issuedAt.getTime() < start.getTime()) {
      opening += inv.total;
    }
  }
  for (const p of payments) {
    if (p.status !== 'success') continue;
    const paidAt = p.paid_at ? new Date(p.paid_at) : new Date(p.created_at);
    if (paidAt.getTime() < start.getTime()) {
      opening -= p.amount;
    }
  }

  // Activity rows in period
  type Row = BalanceForwardRow & { sortAt: number };
  const rows: Row[] = [];

  for (const inv of invoices) {
    const issuedAt = new Date(inv.issue_date + 'T00:00:00');
    if (!inPeriod(inv.issue_date, start, end)) continue;
    rows.push({
      date: inv.issue_date,
      description: `Invoice ${inv.number}`,
      reference: inv.number,
      debit: inv.total,
      credit: 0,
      running_balance: 0,
      sortAt: issuedAt.getTime(),
    });
  }
  for (const p of payments) {
    if (p.status !== 'success') continue;
    const ts = p.paid_at ?? p.created_at;
    if (!inPeriod(ts, start, end)) continue;
    rows.push({
      date: (p.paid_at ?? p.created_at).slice(0, 10),
      description: `Payment received (${p.gateway})`,
      reference: p.gateway_transaction_id,
      debit: 0,
      credit: p.amount,
      running_balance: 0,
      sortAt: new Date(ts).getTime(),
    });
  }

  rows.sort((a, b) => a.sortAt - b.sortAt);

  let running = opening;
  for (const r of rows) {
    running += r.debit - r.credit;
    r.running_balance = running;
  }

  return {
    archetype: 'balance_forward',
    opening_balance: opening,
    closing_balance: running,
    rows: rows.map(({ sortAt: _sortAt, ...rest }) => rest),
  };
}

function computeOpenItem(
  invoices: AdminInvoiceRow[],
  payments: AdminPaymentRow[],
  today: Date
): OpenItemStatement {
  const items: OpenItem[] = [];
  let outstandingTotal = 0;
  for (const inv of invoices) {
    const state = inv.lifecycle_state ?? inv.status;
    if (!OUTSTANDING_STATES.has(state)) continue;
    const paid = payments
      .filter((p) => p.invoice_id === inv.id && p.status === 'success')
      .reduce((sum, p) => sum + p.amount, 0);
    const outstanding = inv.total - paid;
    if (outstanding <= 0) continue;
    const dueDate = inv.due_date ?? inv.period_end ?? inv.issue_date;
    const days = diffDays(dueDate, today);
    items.push({
      invoice_id: inv.id,
      invoice_number: inv.number,
      invoice_date: inv.issue_date,
      due_date: inv.due_date,
      total: inv.total,
      paid,
      outstanding,
      bucket: bucketForDaysOverdue(days),
    });
    outstandingTotal += outstanding;
  }
  items.sort((a, b) => (a.invoice_date < b.invoice_date ? -1 : 1));
  return { archetype: 'open_item', outstanding_total: outstandingTotal, items };
}

function computeActivity(
  invoices: AdminInvoiceRow[],
  payments: AdminPaymentRow[],
  start: Date,
  end: Date
): ActivityStatement {
  const events: ActivityEvent[] = [];
  for (const inv of invoices) {
    if (inPeriod(inv.issue_date, start, end)) {
      events.push({
        event_at: inv.issue_date,
        event_type: 'invoice_issued',
        description: `Invoice ${inv.number} issued`,
        reference: inv.number,
        signed_amount: inv.total,
      });
    }
    if (inv.paid_at && inPeriod(inv.paid_at, start, end)) {
      events.push({
        event_at: inv.paid_at.slice(0, 10),
        event_type: 'invoice_paid',
        description: `Invoice ${inv.number} marked paid`,
        reference: inv.number,
        signed_amount: -inv.total,
      });
    }
  }
  for (const p of payments) {
    if (p.status !== 'success') continue;
    const ts = p.paid_at ?? p.created_at;
    if (!inPeriod(ts, start, end)) continue;
    events.push({
      event_at: ts.slice(0, 10),
      event_type: 'payment_received',
      description: `Payment received via ${p.gateway}`,
      reference: p.gateway_transaction_id,
      signed_amount: -p.amount,
    });
  }
  events.sort((a, b) => (a.event_at < b.event_at ? -1 : 1));
  return { archetype: 'activity', events };
}

export const customerStatementService = {
  compute(
    invoices: AdminInvoiceRow[],
    payments: AdminPaymentRow[],
    period: { start: Date; end: Date }
  ): StatementBundle {
    const today = new Date();
    return {
      aging: computeAging(invoices, payments, today),
      balance_forward: computeBalanceForward(
        invoices,
        payments,
        period.start,
        period.end
      ),
      open_item: computeOpenItem(invoices, payments, today),
      activity: computeActivity(invoices, payments, period.start, period.end),
    };
  },
};
